import { Platform, Share } from 'react-native';

import { INVITE_SHARE, getInviteMessageWithLink } from '@/constants/invites';
import type { ShareLinkKind } from '@/constants/shareLink';

export const EXPERT_LINK_SHARE_TITLE = 'Ask me a question on Primum';

/**
 * Opens the system share sheet for an expert's own link. The link goes in `url` on iOS, where
 * Messages and Mail turn it into a link preview, and in `message` on Android, which ignores
 * `url`.
 *
 * @returns whether the expert completed a share rather than dismissing the sheet.
 */
export async function shareExpertLink(url: string): Promise<boolean> {
  const result = await Share.share(
    Platform.OS === 'ios' ? { url } : { message: url, title: EXPERT_LINK_SHARE_TITLE },
    { dialogTitle: EXPERT_LINK_SHARE_TITLE, subject: EXPERT_LINK_SHARE_TITLE }
  );

  return result.action === Share.sharedAction;
}

/**
 * Opens the system share sheet for an expert's own intake address.
 *
 * It goes in `message` on both platforms rather than `url` as a link does: iOS treats `url`
 * as something to render a preview for, and an address is not that. A `mailto:` would turn
 * it into one, but it would also open the sharer's own mail client in some targets instead
 * of passing the address along, which is the opposite of the intent.
 *
 * @returns whether the expert completed a share rather than dismissing the sheet.
 */
export async function shareIntakeEmail(address: string): Promise<boolean> {
  const result = await Share.share(
    { message: address, title: EXPERT_LINK_SHARE_TITLE },
    { dialogTitle: EXPERT_LINK_SHARE_TITLE, subject: EXPERT_LINK_SHARE_TITLE }
  );

  return result.action === Share.sharedAction;
}

/**
 * Opens the system share sheet with an LMP's referral link, worded exactly as the invites
 * screen and the referral card share it so every invite reads the same.
 *
 * @returns whether the LMP completed a share rather than dismissing the sheet.
 */
export async function shareReferralLink(url: string): Promise<boolean> {
  const result = await Share.share(
    { message: getInviteMessageWithLink(url), title: INVITE_SHARE.TITLE },
    { dialogTitle: INVITE_SHARE.TITLE, subject: INVITE_SHARE.TITLE }
  );

  return result.action === Share.sharedAction;
}

/** Shares the device holder's own link the way its role shares it everywhere else. */
export function shareOwnLink(kind: ShareLinkKind, url: string): Promise<boolean> {
  return kind === 'expert' ? shareExpertLink(url) : shareReferralLink(url);
}
